import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type ScrollableAxis = 'vertical' | 'horizontal' | 'both';

const MIN_THUMB = 32;

@Component({
  selector: 'app-scrollable',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './scrollable.component.html',
  styleUrls: ['./scrollable.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ScrollableComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scrollContent')  scrollContentRef!: ElementRef<HTMLElement>;
  @ViewChild('scrollTrack')    scrollTrackRef!:   ElementRef<HTMLElement>;
  @ViewChild('scrollTrackX')   scrollTrackXRef?:  ElementRef<HTMLElement>;

  enabled = input<boolean>(true);

  axis = input<ScrollableAxis>('vertical');

  /** Distance au bas du contenu (px) à partir de laquelle `scrolledToEnd` est émis. */
  endThreshold = input<number>(100);

  /** Émis quand le défilement approche du bas — utile pour paginer à la demande. */
  scrolledToEnd = output<void>();

  private readonly _cdr = inject(ChangeDetectorRef);

  thumbHeight   = 0;
  thumbTop      = 0;
  showScrollbar = false;

  thumbWidth      = 0;
  thumbLeft       = 0;
  showScrollbarX  = false;

  private _dragAxis: 'x' | 'y' | null = null;
  private _dragStart       = 0;
  private _dragStartScroll = 0;
  private _resizeObserver?: ResizeObserver;
  private _mutationObserver?: MutationObserver;
  private readonly _onMouseMove = this._handleMouseMove.bind(this);
  private readonly _onMouseUp   = this._handleMouseUp.bind(this);

  private _viewReady = false;
  private _active    = false;
  private _endReached = false;

  constructor() {
    effect(() => {
      const enabled = this.enabled();
      this.axis();
      if (!this._viewReady) return;
      if (enabled) { this._enable(); this._computeScrollbar(); }
      else this._disable();
    });
  }

  ngAfterViewInit(): void {
    this._viewReady = true;
    if (this.enabled()) this._enable();
  }

  ngOnDestroy(): void {
    this._teardown();
  }

  get hasVertical(): boolean   { return this.axis() !== 'horizontal'; }
  get hasHorizontal(): boolean { return this.axis() !== 'vertical'; }

  /** Le contenu est observé en plus du conteneur : une liste grandit sans que sa boîte bouge. */
  private _enable(): void {
    if (this._active) return;
    this._active = true;
    requestAnimationFrame(() => requestAnimationFrame(() => this._computeScrollbar()));

    // La fenêtre de défilement garde la même taille quand son contenu grandit :
    // on observe aussi le contenu projeté, sinon la scrollbar n'apparaît qu'au
    // premier redimensionnement (zoom, resize de la fenêtre…).
    this._resizeObserver = new ResizeObserver(() => this._computeScrollbar());
    this._resizeObserver.observe(this.scrollContentRef.nativeElement);
    this._observeProjectedContent();

    // Le contenu peut arriver après coup (données asynchrones) : on réobserve
    // les nouveaux enfants et on recalcule.
    this._mutationObserver = new MutationObserver(() => {
      this._observeProjectedContent();
      this._computeScrollbar();
    });
    this._mutationObserver.observe(this.scrollContentRef.nativeElement, { childList: true });
  }

  private _observeProjectedContent(): void {
    const observer = this._resizeObserver;
    const content = this.scrollContentRef?.nativeElement;
    if (!observer || !content) return;
    for (const child of Array.from(content.children)) observer.observe(child);
  }

  /** Désactive la scrollbar custom (runtime) : teardown + masquage. */
  private _disable(): void {
    if (!this._active) return;
    this._teardown();
    if (this.showScrollbar || this.showScrollbarX) {
      this.showScrollbar  = false;
      this.showScrollbarX = false;
      this._cdr.detectChanges();
    }
  }

  private _teardown(): void {
    this._active = false;
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    this._mutationObserver?.disconnect();
    this._mutationObserver = undefined;
    this._dragAxis = null;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }

  private _computeScrollbar(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) return;

    const needsY = this.hasVertical   && content.scrollHeight > content.clientHeight;
    const needsX = this.hasHorizontal && content.scrollWidth  > content.clientWidth;

    if (needsY !== this.showScrollbar || needsX !== this.showScrollbarX) {
      this.showScrollbar  = needsY;
      this.showScrollbarX = needsX;
      this._cdr.detectChanges();
    }

    if (this.showScrollbar || this.showScrollbarX) requestAnimationFrame(() => this._computeThumb());
  }

  private _computeThumb(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) return;

    const track = this.scrollTrackRef?.nativeElement;
    if (this.showScrollbar && track) {
      this.thumbHeight = Math.max(MIN_THUMB, (content.clientHeight / content.scrollHeight) * track.clientHeight);
    }

    const trackX = this.scrollTrackXRef?.nativeElement;
    if (this.showScrollbarX && trackX) {
      this.thumbWidth = Math.max(MIN_THUMB, (content.clientWidth / content.scrollWidth) * trackX.clientWidth);
    }

    this._updateThumbPosition();
    this._cdr.detectChanges();
  }

  private _updateThumbPosition(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) return;

    const track = this.scrollTrackRef?.nativeElement;
    if (this.showScrollbar && track) {
      const maxScroll = content.scrollHeight - content.clientHeight;
      const ratio     = maxScroll > 0 ? content.scrollTop / maxScroll : 0;
      this.thumbTop   = ratio * (track.clientHeight - this.thumbHeight);
    }

    const trackX = this.scrollTrackXRef?.nativeElement;
    if (this.showScrollbarX && trackX) {
      const maxScroll = content.scrollWidth - content.clientWidth;
      const ratio     = maxScroll > 0 ? content.scrollLeft / maxScroll : 0;
      this.thumbLeft  = ratio * (trackX.clientWidth - this.thumbWidth);
    }
  }

  onScroll(): void {
    this._updateThumbPosition();
    this._checkEndReached();
  }

  /** Ramène le contenu en haut (ex. après un changement de filtre). */
  scrollToTop(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (content) content.scrollTop = 0;
    this._endReached = false;
    this._updateThumbPosition();
  }

  /** N'émet qu'une fois par arrivée en bas, jusqu'à ce qu'on s'en éloigne. */
  private _checkEndReached(): void {
    const content = this.scrollContentRef?.nativeElement;
    if (!content) return;
    const atEnd = content.scrollHeight - content.scrollTop <= content.clientHeight + this.endThreshold();
    if (atEnd && !this._endReached) {
      this._endReached = true;
      this.scrolledToEnd.emit();
    } else if (!atEnd) {
      this._endReached = false;
    }
  }

  onTrackClick(event: MouseEvent): void {
    const track   = this.scrollTrackRef.nativeElement;
    const content = this.scrollContentRef.nativeElement;
    const clickY  = event.clientY - track.getBoundingClientRect().top;
    content.scrollTop = (clickY / track.clientHeight) * (content.scrollHeight - content.clientHeight);
    this._updateThumbPosition();
  }

  onTrackClickX(event: MouseEvent): void {
    const track   = this.scrollTrackXRef?.nativeElement;
    const content = this.scrollContentRef.nativeElement;
    if (!track) return;
    const clickX  = event.clientX - track.getBoundingClientRect().left;
    content.scrollLeft = (clickX / track.clientWidth) * (content.scrollWidth - content.clientWidth);
    this._updateThumbPosition();
  }

  onThumbMouseDown(event: MouseEvent): void {
    this._startDrag(event, 'y');
  }

  onThumbMouseDownX(event: MouseEvent): void {
    this._startDrag(event, 'x');
  }

  private _startDrag(event: MouseEvent, axis: 'x' | 'y'): void {
    event.preventDefault();
    event.stopPropagation();
    const content = this.scrollContentRef.nativeElement;
    this._dragAxis        = axis;
    this._dragStart       = axis === 'y' ? event.clientY : event.clientX;
    this._dragStartScroll = axis === 'y' ? content.scrollTop : content.scrollLeft;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup',   this._onMouseUp);
  }

  private _handleMouseMove(event: MouseEvent): void {
    if (!this._dragAxis) return;
    const content = this.scrollContentRef.nativeElement;

    if (this._dragAxis === 'y') {
      const track = this.scrollTrackRef?.nativeElement;
      if (!track) return;
      const delta = event.clientY - this._dragStart;
      const ratio = (content.scrollHeight - content.clientHeight) / (track.clientHeight - this.thumbHeight);
      content.scrollTop = this._dragStartScroll + delta * ratio;
    } else {
      const track = this.scrollTrackXRef?.nativeElement;
      if (!track) return;
      const delta = event.clientX - this._dragStart;
      const ratio = (content.scrollWidth - content.clientWidth) / (track.clientWidth - this.thumbWidth);
      content.scrollLeft = this._dragStartScroll + delta * ratio;
    }

    this._updateThumbPosition();
  }

  private _handleMouseUp(): void {
    this._dragAxis = null;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }
}
